/** Hook for mapping computer keyboard events to noteOn/noteOff.
 *
 * Handles keydown/keyup events with repeat guard, and supports
 * octave shifting with Z/X keys and semitone shifting with C/V keys.
 */

import { useCallback, useEffect, useRef } from "react";
import {
	getNoteForKey,
	OCTAVE_DOWN_KEY,
	OCTAVE_UP_KEY,
} from "../lib/keymap.ts";
import type { TuningState } from "../lib/stateStorage.ts";
import { trace } from "../lib/synthTracer.ts";

/** Props for the useKeyboardInput hook. */
export type UseKeyboardInputProps = {
	readonly noteOn: (note: number) => void;
	readonly noteOff: (note: number) => void;
	readonly enabled: boolean;
	readonly tuning: TuningState;
	readonly setTuning: (tuning: Partial<TuningState>) => void;
};

/** Hook that maps keyboard events to synth note events. */
export function useKeyboardInput({
	noteOn,
	noteOff,
	enabled,
	tuning,
	setTuning,
}: UseKeyboardInputProps): void {
	const heldKeysRef = useRef<Map<string, number>>(new Map());

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!enabled) {
				trace("keyboard", "keyDown:disabled", { key: event.key });
				return;
			}
			if (event.repeat) return;

			// Don't capture keyboard events when user is typing in text inputs.
			const target = event.target as HTMLElement;
			if (
				(target.tagName === "INPUT" &&
					(target as HTMLInputElement).type !== "range") ||
				target.tagName === "TEXTAREA" ||
				target.tagName === "SELECT"
			) {
				return;
			}

			const key = event.key.toLowerCase();

			// Octave shift
			if (key === OCTAVE_DOWN_KEY) {
				setTuning({ octaveOffset: Math.max(tuning.octaveOffset - 1, -3) });
				return;
			}
			if (key === OCTAVE_UP_KEY) {
				setTuning({ octaveOffset: Math.min(tuning.octaveOffset + 1, 3) });
				return;
			}

			// Note on — apply octave + semitone offset
			const totalSemitones = tuning.octaveOffset * 12 + tuning.semitoneOffset;
			const note = getNoteForKey(key, 0);
			if (note !== undefined && !heldKeysRef.current.has(key)) {
				const adjusted = note + totalSemitones;
				trace("keyboard", "noteOn", { key, note: adjusted, tuning });
				heldKeysRef.current.set(key, adjusted);
				noteOn(adjusted);
			} else if (note === undefined) {
				trace("keyboard", "keyDown:unmapped", { key });
			} else {
				trace("keyboard", "keyDown:alreadyHeld", { key, note });
			}
		},
		[enabled, noteOn, tuning, setTuning],
	);

	const handleKeyUp = useCallback(
		(event: KeyboardEvent) => {
			if (!enabled) return;

			const key = event.key.toLowerCase();
			const note = heldKeysRef.current.get(key);
			if (note !== undefined) {
				trace("keyboard", "noteOff", { key, note });
				heldKeysRef.current.delete(key);
				noteOff(note);
			} else {
				trace("keyboard", "keyUp:notHeld", { key });
			}
		},
		[enabled, noteOff],
	);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("keyup", handleKeyUp);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("keyup", handleKeyUp);
		};
	}, [handleKeyDown, handleKeyUp]);

	// Release all held notes when disabled
	useEffect(() => {
		if (!enabled) {
			for (const [, note] of heldKeysRef.current) {
				noteOff(note);
			}
			heldKeysRef.current.clear();
		}
	}, [enabled, noteOff]);
}
